import { create, all, type MathJsInstance } from 'mathjs'

/**
 * One hardened mathjs instance, shared by BOTH:
 *  - the AI's `calculate` tool (server), and
 *  - the user-facing scientific calculator sheet (client).
 * They can never diverge because they import the same evaluator.
 */
const math = create(all, {}) as MathJsInstance

// Harden the expression parser: disable only the functions that can mutate the
// environment or define globals from INSIDE a user/AI expression. We must NOT
// disable `parse` itself — evaluateExpression relies on it below.
const disabled = () => {
  throw new Error('Function is disabled for security reasons')
}
math.import(
  {
    import: disabled,
    createUnit: disabled,
  },
  { override: true },
)

export interface CalcResult {
  expression: string
  result: string
  value: unknown
}

export function evaluateExpression(
  expression: string,
  scope: Record<string, number> = {},
  precision = 12,
): CalcResult {
  const expr = expression.trim()
  if (!expr) throw new Error('Empty expression')
  if (expr.length > 1000) throw new Error('Expression too long')

  const node = math.parse(expr)
  // Fresh scope copy every call — never reuse a shared mutable scope.
  const value = node.evaluate({ ...scope })
  const result =
    typeof value === 'undefined'
      ? ''
      : math.format(value, { precision, lowerExp: -9, upperExp: 12 })
  return { expression: expr, result, value }
}

export { math }
